import eachElement from 'Utils/eachElement.js';
import { merge } from 'lodash';

/* Import Chart.js */

import {
	Chart,
	BarController,
	BarElement,
	CategoryScale,
	LinearScale,
	DoughnutController,
	ArcElement,
	Legend,
	Tooltip,
} from 'chart.js';


/* Plugins */

import ChartDeferred from 'chartjs-plugin-deferred';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartjsPluginStacked100 from 'chartjs-plugin-stacked100';
import htmlLegendPlugin from './legends.js';
import externalTooltipHandler from './tooltips.js';

Chart.register(
	BarController,
	BarElement,
	CategoryScale,
	LinearScale,
	DoughnutController,
	ArcElement,
	Legend,
	Tooltip,
);

Chart.register(ChartDeferred);
Chart.register(ChartDataLabels);
Chart.register(ChartjsPluginStacked100);
Chart.register(htmlLegendPlugin);


/* Global config */

Chart.defaults.font.family = 'Inter, sans-serif';
Chart.defaults.font.size = '14';
Chart.defaults.color = '#2a303b';

Chart.defaults.set('plugins.datalabels', {
	formatter: (_value, context) => getPercentage(context),
	color: '#fff',
	font: {
		size: 16,
		weight: 700,
	},
	display: 'auto',
	clip: true,
});


/* Convert raw data to display percentage */
const getPercentage = (context, value = false) => {
	const { datasetIndex, dataIndex, chart } = context;

	/* If there's original data (after stacked100 has transformed it), use that */
	const data = 'originalData' in chart.data ? chart.data.originalData : chart.data.datasets;
	/* Normalise the two different data structures to find the true value */
	const dataPoint = value || ('data' in data[datasetIndex] ? data[datasetIndex].data[dataIndex] : data[datasetIndex][dataIndex]);
	/* Find the total from the dataset */
	const total = context.chart.data.datasets[context.datasetIndex].total[context.dataIndex];

	/* Display percentage */
	if (data[datasetIndex].isPercentage) {
		return `${Math.round(dataPoint * 100)}%`;
	}

	return `${Math.round((dataPoint / total) * 100)}%`;
};


/* Find or calculate the most appropriate total for a given datapoint
   for calculating a percentage */

const getTotal = (data, datapoint, index, total, isStacked) => {
	/* If there's a datapoint-specific total; otherwise use chart total */
	const concernedTotal = 'total' in datapoint ? datapoint.total : total;

	if (isStacked) {
		/* If it's a stacked chart */
		return datapoint.value.reduce((sum, number_) => sum + number_, 0);
	}

	if (!concernedTotal) {
		/* If there was no explicit total, calculate it from the dataset */
		return data.reduce((accumulator, row) => accumulator + (Array.isArray(row.value) ? row.value[index] : row.value), 0);
	}

	if (Array.isArray(concernedTotal)) {
		/* If there was a total for a two-dimensional chart, pick the correct one */
		return concernedTotal[index];
	}

	/* Otherwise, assume found total is good as-is */
	return concernedTotal;
};


/* Canvas font string matching the chart axis labels */
const labelFont = () => `${Chart.defaults.font.size}px ${Chart.defaults.font.family}`;

/* Wrap a label string into multiple lines fitting within maxWidth pixels */
const wrapLabel = (label, maxWidth, ctx) => {
	ctx.save();
	ctx.font = labelFont();

	const lines = [];
	/* Respect any explicit line breaks first */
	for (const segment of String(label).split('\n')) {
		let current = '';
		for (const word of segment.split(' ')) {
			const candidate = current ? `${current} ${word}` : word;
			if (current && ctx.measureText(candidate).width > maxWidth) {
				lines.push(current);
				current = word;
			} else {
				current = candidate;
			}
		}

		if (current) {
			lines.push(current);
		}
	}

	ctx.restore();
	return lines.length > 0 ? lines : [String(label)];
};


/* Tuning for bar-chart label columns. Charts are bucketed into up to three
   shared widths: narrow (short single-line labels), wide (longer/two-line
   labels), and extra-wide (real outliers). */
const LABEL_WIDTH_CAP_FRACTION = 0.4; /* Hard ceiling, as a fraction of the chart width */
const LABEL_NARROW_PERCENTILE = 0.35; /* Narrow tier fits this proportion of charts on one line */
const LABEL_WIDE_PERCENTILE = 0.7; /* Wide tier covers most of the remaining charts */
const LABEL_NARROW_MAX_LINES = 2; /* A chart stays in the narrow tier if its labels wrap within this many lines */
const LABEL_OUTLIER_FENCE = 1.5; /* Tukey's fence multiplier for flagging outlier charts */

/* Linear-interpolated percentile of a sorted (ascending) numeric array */
const percentile = (sorted, p) => {
	if (sorted.length === 0) {
		return 0;
	}

	const index = (sorted.length - 1) * p;
	const lower = Math.floor(index);
	const upper = Math.ceil(index);
	if (lower === upper) {
		return sorted[lower];
	}

	return sorted[lower] + ((sorted[upper] - sorted[lower]) * (index - lower));
};

/* Work out an appropriate label-column width for every bar chart on the page
   (including grouped and stacked bars; only pie/doughnut charts are excluded).
   To keep charts aligned without forcing short-labelled charts into a needlessly
   wide column, charts are bucketed into up to three shared widths:
     - narrow: labels that fit within a couple of lines (e.g. "Plan", "Build");
     - wide: longer labels that would need more lines in the narrow column;
     - extra-wide: the few real outliers with unusually long labels.
   A chart is kept in the narrow tier whenever its labels wrap within the line
   limit there, so a chart that only needs a second line isn't bumped wider.
   Widths are derived from percentiles of the measured label widths, with
   outliers detected via Tukey's fence. Returns a Map of canvas -> width (px). */
const measureLabelWidths = () => {
	const temporaryCtx = document.createElement('canvas').getContext('2d');
	temporaryCtx.font = labelFont();

	let refChartWidth = 0;
	const charts = [];

	for (const instance of document.querySelectorAll('.chart')) {
		const canvas = instance.querySelector('canvas');
		if (!canvas || !('data' in canvas.dataset)) {
			continue;
		}

		if ((canvas.dataset.type || 'bar') !== 'bar') {
			continue;
		}

		if (!refChartWidth) {
			refChartWidth = instance.querySelector('.chart-container')?.offsetWidth ?? 0;
		}

		/* The chart's natural width is its longest single (unwrapped) label */
		const data = JSON.parse(canvas.dataset.data);
		const labels = data.map(row => String(row.label));
		let naturalWidth = 0;
		for (const label of labels) {
			for (const segment of label.split('\n')) {
				naturalWidth = Math.max(naturalWidth, temporaryCtx.measureText(segment).width);
			}
		}

		charts.push({ canvas, naturalWidth, labels });
	}

	/* Round up and clamp a width to the hard ceiling (a fraction of the chart) */
	const cap = refChartWidth ? refChartWidth * LABEL_WIDTH_CAP_FRACTION : Number.POSITIVE_INFINITY;
	const capped = value => Math.ceil(Math.min(value, cap));

	const naturalWidths = charts.map(chart => chart.naturalWidth).sort((a, b) => a - b);
	const cappedMax = capped(naturalWidths[naturalWidths.length - 1] ?? 0);
	const widthByCanvas = new Map();

	/* With too few charts there aren't meaningful tiers; size everything
	   uniformly to the widest label (capped). */
	if (charts.length < 4) {
		for (const chart of charts) {
			widthByCanvas.set(chart.canvas, cappedMax);
		}

		return widthByCanvas;
	}

	/* Detect outlier charts (unusually long labels) via Tukey's fence */
	const q1 = percentile(naturalWidths, 0.25);
	const q3 = percentile(naturalWidths, 0.75);
	const fence = q3 + (LABEL_OUTLIER_FENCE * (q3 - q1));

	/* The two shared tiers are sized from the non-outlier charts, so the few
	   very long labels don't inflate them. */
	const typical = naturalWidths.filter(width => width <= fence);
	const narrowWidth = capped(percentile(typical, LABEL_NARROW_PERCENTILE));
	const wideWidth = Math.max(narrowWidth, capped(percentile(typical, LABEL_WIDE_PERCENTILE)));
	const extraWideWidth = Math.max(wideWidth, cappedMax);

	/* Assign each chart to the smallest tier that suits its labels. A chart stays
	   in the narrow tier if every label wraps within the line limit there, so
	   charts that only need a second line don't get bumped to the wider tier. */
	for (const chart of charts) {
		const fitsNarrow = chart.labels.every(
			label => wrapLabel(label, narrowWidth, temporaryCtx).length <= LABEL_NARROW_MAX_LINES,
		);

		let width = wideWidth;
		if (fitsNarrow) {
			width = narrowWidth;
		} else if (chart.naturalWidth > fence) {
			width = extraWideWidth;
		}

		widthByCanvas.set(chart.canvas, width);
	}

	return widthByCanvas;
};


/* Set up charts on the page */

const setUpCharts = initOptions => {
	const labelWidths = measureLabelWidths();

	eachElement('.chart', instance => {
		const canvas = instance.querySelector('canvas');
		const legend = instance.querySelector('figcaption');

		const section = instance.closest('section');
		const colors = initOptions.getColors(section, canvas.dataset);

		if (canvas && legend && 'data' in canvas.dataset) {
			/* Raw data from component */
			const data = JSON.parse(canvas.dataset.data);
			/* Group labels for stacked bar chart */
			const dataLabels = JSON.parse(canvas.dataset.labels || '[""]');
			/* Figure representing 100% of the data range */
			const total = JSON.parse(canvas.dataset.total || 'false');
			/* Type of chart */
			let type = canvas.dataset.type || 'bar';
			/* Labels for datasets */
			let labels;
			/* Datasets to display */
			let datasets;

			/* Global chart options */
			let options = {
				maintainAspectRatio: false,
				plugins: {
					/* Render legend in HTML outside of the canvas */
					htmlLegend: {
						container: legend,
					},
					/* Don't render the default legend */
					legend: {
						display: false,
					},
					/* Render tooltip as HTML */
					tooltip: {
						enabled: false,
						position: 'nearest',
						external: externalTooltipHandler,

						callbacks: {
							label(context) {
								return getPercentage(context, context.raw);
							},
						},
					},
				},
			};

			if (type === 'bar') {
				/* Load labels */
				labels = data.map(row => row.label);

				/* Shared label-column width (or local override for outlier charts) */
				const labelWidth = labelWidths.get(canvas) ?? 0;

				/* If each data point has multiple values, figure out if it's a stacked bar chart */
				const hasMultipleValues = Array.isArray(data[0].value);
				const isStacked = 'stacked' in canvas.dataset ? JSON.parse(canvas.dataset.stacked) : hasMultipleValues;

				/* Calculate datasets differently based on if this is a stacked bar chart or not */
				if (isStacked) {
					datasets = dataLabels.map((label, index) => ({
						label,
						data: data.map(row => row.value[index]),
						total: data.map(row => getTotal(data, row, index, total, isStacked)),
						backgroundColor: colors[index],
					}));
				} else if (hasMultipleValues) {
					datasets = dataLabels.map((label, index) => ({
						label,
						data: data.map(row => row.value[index] / getTotal(data, row, index, total, isStacked)),
						total: data.map(row => getTotal(data, row, index, total, isStacked)),
						isPercentage: true,
						backgroundColor: colors[index],
					}));

					/* Flag grouped bar charts so the legend can be displayed */
					instance.classList.add('is-grouped');
				} else {
					datasets = [{
						label: '',
						data: data.map((row, index) => row.value / getTotal(data, row, index, total)),
						total: data.map((row, index) => getTotal(data, row, index, total)),
						isPercentage: true,
						backgroundColor: colors,
					}];

					/* Generate legend based on labels, not datasets */
					options.plugins.legend.labels = {
						generateLabels: chart => {
							const datasets = chart.data.datasets;
							return datasets[0].data.map((data, i) => {
								const label = chart.data.labels[i];
								const text = Array.isArray(label) ? label.join(' ').replace(/\s\s+/g, ' ') : label;
								return {
									text,
									fillStyle: datasets[0].backgroundColor[i],
									index: i,
								};
							});
						},
					};
				}

				/* Enable stacking */
				if (isStacked) {
					options.plugins.stacked100 = {
						enable: true,
						replaceTooltipLabel: false,
					};
					instance.classList.add('is-stacked');
				}

				/* Make bar chart horizontal */
				options.indexAxis = 'y';

				options.barPercentage = 1;
				options.categoryPercentage = 0.9;

				options.plugins.datalabels = {
					/* Align in-data labels to edge */
					anchor: 'end',
					align: context => (!isStacked && context.dataset.data[context.dataIndex] <= 0.05) ? 'right' : 'left',
					color: context => {
						const data = context.dataset;
						const bgColor = Array.isArray(data.backgroundColor) ? data.backgroundColor[context.dataIndex] : data.backgroundColor;
						return (!isStacked && data.data[context.dataIndex] <= 0.05) ? bgColor : '#fff';
					},
					display: context => {
						if (isStacked) {
							const { datasetIndex, dataIndex, chart } = context;
							const data = 'originalData' in chart.data ? chart.data.originalData : chart.data.datasets;
							const dataPoint = ('data' in data[datasetIndex] ? data[datasetIndex].data[dataIndex] : data[datasetIndex][dataIndex]);
							return dataPoint / context.dataset.total[dataIndex] > 0.05;
						}

						return 'auto';
					},
				};

				/* Remove grid lines, set stacking if needed */
				options.scales = {
					x: {
						stacked: isStacked,
						grid: {
							drawOnChartArea: false,
							drawTicks: false,
						},
						ticks: {
							display: false,
						},
						border: {
							display: false,
						},
					},
					y: {
						stacked: isStacked,
						grid: {
							drawOnChartArea: false,
							drawTicks: false,
						},
						border: {
							display: false,
						},
						/* Force every bar chart's label column to the same width so
						   they line up across the page. Only the category axis is
						   affected, leaving value axes (e.g. vertical charts) alone. */
						afterFit(scale) {
							if (scale.type === 'category') {
								scale.width = labelWidth + 8;
							}
						},
						ticks: {
							padding: 8,
							callback(value) {
								return wrapLabel(this.getLabelForValue(value), labelWidth, this.ctx);
							},
						},
						alignToPixels: true,
					},
				};

				/* Set container height. Grouped bar charts render one bar per
				   dataset within each category, so reserve vertical space per
				   individual bar to guarantee every value label has room to
				   render; other charts reserve space per category row. */
				const isGrouped = !isStacked && datasets.length > 1;
				const containerHeight = isGrouped
					? 64 + (datasets.length * datasets[0].data.length * 26) + (datasets[0].data.length * 12)
					: 64 + ((datasets.length + datasets[0].data.length) * 48);
				instance.querySelector('.chart-container').style.height = `${containerHeight}px`;
			} else if (type === 'pie' || type === 'donut' || type === 'doughnut') {
				/* Standardise names */
				type = 'doughnut';

				/* Calculate data */
				labels = data.map(row => row.label);
				datasets = [{
					label: '',
					data: data.map(row => row.value),
					total: data.map((row, index) => getTotal(data, row, index, total)),
					backgroundColor: colors,
				}];

				/* Remove gaps between segments */
				options.borderWidth = 0;

				/* Adjust the size of the doughnut hole */
				options.cutout = '58%';
			}

			/* Add standardised chart type to DOM */
			instance.classList.add(`is-type-${type}`);

			let processedData = { labels, datasets };

			/* Overwrite any config options */
			if (canvas.dataset.config && 'configs' in initOptions && initOptions.configs[canvas.dataset.config]) {
				const config = initOptions.configs[canvas.dataset.config];
				options = merge(options, config);

				/* Post-process data, if applicable */
				if ('data' in initOptions && initOptions.data[canvas.dataset.config]) {
					processedData = initOptions.data[canvas.dataset.config](processedData, data, instance);
				}
			}

			/* Create chart */
			instance.chartInstance = new Chart(
				canvas,
				{
					type,
					data: processedData,
					options,
				},
			);

			/* Reload the chart when the font has loaded, to ensure proper display */
			document.fonts.onloadingdone = () => {
				instance.chartInstance.update();
			};
		}
	});
};

export default {
	setUpCharts,
};
